// グリッド/リスト選択とフォルダドロップ移動
import { ref } from 'vue';
import { filterVideoItems } from './useItemTypeGuards';

export const useDragAndDrop = ({
  items,
  isInShareBasket,
  shareBasketVideoIds,
  setShareBasketItems,
  selectItem,
  clearSelection,
  selectFolder,
  selectedFolderId,
  allowedFolderIds,
  removeFromShareBasket,
}) => {
  const moveEndpoint = ref('');
  const moveFailedMessage = ref('Failed to move files.');
  const isDragging = ref(false);
  const dragTargetFolderId = ref(null);
  // TOP のドロップ先ハイライトは dragTargetFolderId (null="ドロップ先なし"の意味で使用中) を
  // 流用せず専用の ref で管理する。流用すると「どこもホバーしていない間ずっとTOPが光る」誤点灯になる。
  const isTopDropTarget = ref(false);
  const dragVideoIds = ref([]);
  let dragPreviewEl = null;

  const setMoveOptions = ({ endpoint = '', failedMessage = null } = {}) => {
    moveEndpoint.value = endpoint || '';
    if (failedMessage) {
      moveFailedMessage.value = failedMessage;
    }
  };

  const cleanupDragPreview = () => {
    if (dragPreviewEl && dragPreviewEl.parentNode) {
      dragPreviewEl.parentNode.removeChild(dragPreviewEl);
    }
    dragPreviewEl = null;
  };

  const getSelectedItems = () => {
    const idSet = new Set(dragVideoIds.value.map((id) => String(id)));
    const list = filterVideoItems(items.value);
    return list.filter((item) => idSet.has(String(item?.id)));
  };

  const createDragPreview = (selectedItems) => {
    if (!Array.isArray(selectedItems) || selectedItems.length < 1) {
      return null;
    }
    const first = selectedItems[0] || {};
    const preview = document.createElement('div');
    preview.className = 'drag-preview';
    preview.style.position = 'fixed';
    preview.style.top = '-1000px';
    preview.style.left = '-1000px';
    preview.style.pointerEvents = 'none';

    const thumb = document.createElement('div');
    thumb.className = 'drag-preview__thumb';
    if (first.thumbnail_url) {
      thumb.style.backgroundImage = `url("${first.thumbnail_url}")`;
    } else {
      thumb.classList.add('is-empty');
    }

    const count = document.createElement('div');
    count.className = 'drag-preview__count';
    count.textContent = String(selectedItems.length);

    preview.appendChild(thumb);
    preview.appendChild(count);
    document.body.appendChild(preview);
    return preview;
  };

  const isFolderAllowed = (folderId) => {
    if (allowedFolderIds.value === null) {
      return true;
    }
    const numericId = Number(folderId);
    return (allowedFolderIds.value || []).some((id) => Number(id) === numericId);
  };

  const resolveMovableIds = (targetFolderId, ids) => {
    const targetId = String(targetFolderId ?? '');
    const idSet = new Set((ids || []).map((id) => String(id)));
    const list = filterVideoItems(items.value);
    return list
      .filter((item) => idSet.has(String(item?.id)))
      .filter((item) => String(item?.folder_id ?? '') !== targetId)
      .map((item) => item.id);
  };

  const moveVideosToFolder = async (folderId, ids) => {
    if (!moveEndpoint.value) {
      return;
    }
    if (!ids.length) {
      return;
    }
    try {
      const currentFolderId = selectedFolderId.value ?? null;
      const payload = folderId === null
        ? { to_root: 1, video_ids: ids }
        : { folder_id: folderId, video_ids: ids };
      await window.axios.post(moveEndpoint.value, payload);
      clearSelection();
      ids.forEach((id) => removeFromShareBasket(id));
      await selectFolder(currentFolderId);
    } catch (error) {
      console.error('Move failed:', error);
      window.alert(moveFailedMessage.value || 'Failed to move files.');
    }
  };

  const handleFolderDrop = async (folderId) => {
    if (!isDragging.value) {
      return;
    }
    dragTargetFolderId.value = null;
    isDragging.value = false;
    cleanupDragPreview();

    if (!isFolderAllowed(folderId)) {
      return;
    }
    const ids = dragVideoIds.value.length
      ? dragVideoIds.value
      : Array.from(shareBasketVideoIds.value);
    const movableIds = resolveMovableIds(folderId, ids);
    if (!movableIds.length) {
      return;
    }
    await moveVideosToFolder(folderId, movableIds);
    dragVideoIds.value = [];
  };

  const handleFolderDragEnter = (folderId, event) => {
    if (!isDragging.value) {
      return;
    }
    if (!isFolderAllowed(folderId)) {
      return;
    }
    dragTargetFolderId.value = folderId;
  };

  const handleFolderDragLeave = (folderId, event) => {
    if (event && event.relatedTarget && event.currentTarget
      && typeof event.currentTarget.contains === 'function'
      && event.currentTarget.contains(event.relatedTarget)) {
      return;
    }
    if (dragTargetFolderId.value === folderId) {
      dragTargetFolderId.value = null;
    }
  };

  const handleTopDrop = async () => {
    if (!isDragging.value) {
      return;
    }
    isTopDropTarget.value = false;
    isDragging.value = false;
    cleanupDragPreview();

    if (!isFolderAllowed(null)) {
      return;
    }
    const ids = dragVideoIds.value.length
      ? dragVideoIds.value
      : Array.from(shareBasketVideoIds.value);
    const movableIds = resolveMovableIds(null, ids);
    if (!movableIds.length) {
      return;
    }
    await moveVideosToFolder(null, movableIds);
    dragVideoIds.value = [];
  };

  const handleTopDragEnter = (event) => {
    if (!isDragging.value) {
      return;
    }
    if (!isFolderAllowed(null)) {
      return;
    }
    isTopDropTarget.value = true;
  };

  const handleTopDragLeave = (event) => {
    if (event && event.relatedTarget && event.currentTarget
      && typeof event.currentTarget.contains === 'function'
      && event.currentTarget.contains(event.relatedTarget)) {
      return;
    }
    isTopDropTarget.value = false;
  };

  const handleDragStart = (item, index, event) => {
    if (!item) {
      return;
    }
    const target = event?.target;
    if (target && target.closest && target.closest('input[type="checkbox"]')) {
      event.preventDefault();
      return;
    }
    if (!isInShareBasket(item.id)) {
      setShareBasketItems([item]);
      selectItem(item, index);
    }
    const list = filterVideoItems(items.value);
    const visibleIds = new Set(list.map((entry) => String(entry?.id)));
    const ids = Array.from(shareBasketVideoIds.value).filter((id) => visibleIds.has(String(id)));
    dragVideoIds.value = ids;
    isDragging.value = true;
    if (event?.dataTransfer) {
      const selectedItems = getSelectedItems();
      cleanupDragPreview();
      if (selectedItems.length > 0) {
        dragPreviewEl = createDragPreview(selectedItems);
        if (dragPreviewEl) {
          const offsetX = dragPreviewEl.offsetWidth / 2;
          const offsetY = dragPreviewEl.offsetHeight - 8;
          event.dataTransfer.setDragImage(dragPreviewEl, offsetX, offsetY);
        }
      }
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', ids.join(','));
    }
  };

  const handleDragEnd = () => {
    isDragging.value = false;
    dragTargetFolderId.value = null;
    isTopDropTarget.value = false;
    dragVideoIds.value = [];
    cleanupDragPreview();
  };

  return {
    moveFailedMessage,
    isDragging,
    dragTargetFolderId,
    isTopDropTarget,
    setMoveOptions,
    handleDragStart,
    handleDragEnd,
    handleFolderDrop,
    handleFolderDragEnter,
    handleFolderDragLeave,
    handleTopDrop,
    handleTopDragEnter,
    handleTopDragLeave,
  };
};
